<script setup>
import { ref, computed, onMounted } from "vue"
import { storeToRefs } from 'pinia'
import { usePersonalizationRuleStore } from '@/stores/personalization'
import { usePortalsStore } from '@/stores/portals'
import CreatePersonalizationRuleForm from '@/components/policies/CreatePersonalizationRuleForm.vue'
import PersonalizationRulesTable from '@/components/policies/PersonalizationRulesTable.vue'

const personalizationRuleStore = usePersonalizationRuleStore()
const portalsStore = usePortalsStore()

const {
  isLoadingActions,
  isLoadingConditionFields,
  actions,
  conditionFields
} = storeToRefs(personalizationRuleStore)
const { selectedPortal } = storeToRefs(portalsStore)

const isLoading = computed(() => (isLoadingActions.value || isLoadingConditionFields.value))

const showCreateRuleForm = ref(false)

const createRuleFormInitialValues = ref(null)

const canEditPersonalizationRules = computed(() => (
  selectedPortal.value.fields.policies.personalization.$rules.editable
))

function addRule(rule) {
  const rules = selectedPortal.value.fields.policies.personalization.$rules.inputValue || []
  rules.push(rule)
  selectedPortal.value.fields.policies.personalization.$rules.inputValue = rules
  showCreateRuleForm.value = false
  createRuleFormInitialValues.value = null
}

function cancelRule() {
  showCreateRuleForm.value = false
  createRuleFormInitialValues.value = null
}

function copyRule(rule) {
  const { name, actions, conditions } = rule
  showCreateRuleForm.value = true
  createRuleFormInitialValues.value = {
    name: `Copy of ${name}`,
    status: 'active',
    actions,
    conditions,
    published_at: null
  }
}

onMounted(() => {
  personalizationRuleStore.fetchActions()
  personalizationRuleStore.fetchConditionFields()
})
</script>

<template>
    <p class="mb-5">
    Create rules to personalize the returns policy for certain customers or products. These rules will <br />
    override your default Policies and Settings. Learn more about <a href="#">Policy Personalization</a>.
    </p>
    <CreatePersonalizationRuleForm
      v-if="showCreateRuleForm"
      :initialValues="createRuleFormInitialValues"
      :actions="actions"
      :conditionFields="conditionFields"
      :disabled="isLoading || !canEditPersonalizationRules"
      @click:create="addRule"
      @click:cancel="cancelRule"
    />
    <div v-else>
      <div class="d-flex align-items-center gap-3 mb-4">
        <h3 class="my-0">All Rules</h3>
        <div class="border-start border-secondary">&nbsp;</div>
        <button
          class="btn btn btn-link fw-semibold text-decoration-none btn-create-rule p-0"
          type="button"
          :disabled="!canEditPersonalizationRules"
          @click="() => showCreateRuleForm = true"
        >
          CREATE RULE
        </button>
      </div>
      <PersonalizationRulesTable
        :disabled="!canEditPersonalizationRules"
        @click:copy="copyRule"
      />
    </div>
</template>

<style lang="scss" scoped>
.btn-create-rule {
  min-width: unset;
}
</style>
